import { useState, useEffect, useRef } from 'react';
import {
  isThemeParamsMounted,
  themeParams,
  useLaunchParams,
  useSignal,
} from '@telegram-apps/sdk-react';
import { AppRoot } from '@telegram-apps/telegram-ui';
import { LOCALE_CODES, RTL_LOCALES, type Locale, type ReferralChannel } from '@veylme/shared';

import { parseStartParam } from './lib/tg.js';
import { getAuthDateSec } from './lib/authDate.js';
import {
  captureLiveStartParam,
  clearRestartMark,
  resolveStartParam,
} from './lib/launchContext.js';
import {
  shouldShowTabbar,
  subscribeAuthFailure,
  type SessionFailure,
} from './lib/sessionExpiry.js';
import { mapPlatform } from './lib/platform.js';
import { LocaleProvider, SetLocaleProvider } from './i18n/LocaleContext.js';
import { detectLocale } from './i18n/detectLocale.js';
import { getLocalePref, updateLocalePref, emitEvent, getAdminStatus, postAdSource } from './api/client.js';
import { AskByCodeScreen } from './screens/AskByCodeScreen.js';
import { AskResolvingScreen } from './screens/AskResolvingScreen.js';
import { AskScreen } from './screens/AskScreen.js';
import { BottomTabbar, type TabKey } from './screens/BottomTabbar.js';
import { InboxScreen } from './screens/InboxScreen.js';
import { LeaderboardScreen } from './screens/LeaderboardScreen.js';
import { MyQuestionsScreen } from './screens/MyQuestionsScreen.js';
import { RewardsScreen } from './screens/RewardsScreen.js';
import { SettingsScreen } from './screens/SettingsScreen.js';
import { ShareScreen } from './screens/ShareScreen.js';
import { AnalyticsScreen } from './screens/AnalyticsScreen.js';
import { SessionExpiredScreen } from './screens/SessionExpiredScreen.js';
import { SessionAgeWarning } from './components/SessionAgeWarning.js';
import { ToastProvider } from './components/ToastProvider.js';
import styles from './App.module.css';

const LOCALE_SET = new Set<string>(LOCALE_CODES);

/** Читает сохранённый язык из localStorage ('veylme.locale'). */
function readStoredLocale(): Locale | null {
  try {
    const v = localStorage.getItem('veylme.locale');
    if (v && LOCALE_SET.has(v)) return v as Locale;
  } catch {
    // localStorage недоступен (SSR / sandboxed iframe)
  }
  return null;
}

/** Выставляет dir/lang документа под выбранный язык (RTL для ar/fa/he). */
function applyDocumentLocale(locale: Locale): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.lang = locale;
  el.dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

/**
 * Корневой компонент Mini App. State machine на 7 экранов (ASK / ASK_BY_CODE /
 * INBOX / SHARE / LEADERBOARD / REWARDS / SETTINGS) — без react-router'а. Ответ автор
 * пишет инлайн прямо в карточке вопроса (InboxScreen/QuestionCard); отдельного
 * экрана ANSWER больше нет — после отправки на месте появляется пузырь ответа
 * и кнопка «Поделиться», ведущая на SHARE.
 *
 * Веха 9: добавлены LEADERBOARD и REWARDS (лидерборд + награды).
 * Нижняя навигация (Tabbar) показывается на верхнеуровневых вкладках
 * (INBOX / LEADERBOARD / REWARDS / SETTINGS); под-потоки ASK/ANSWER/SHARE
 * рендерятся без неё.
 *
 * work-036 (Трек D): MESSAGES — под-поток «Полученные сообщения» из Settings
 * (как ANALYTICS/MY_QUESTIONS — свой BackButton, вне Tabbar); переиспользует
 * InboxScreen с `boxFormat="message"`.
 */

/** Referral-контекст: с какого бокса+канала пришёл новый юзер (Веха 8 M2). */
type RefContext = { sourceBoxToken: string; channel: ReferralChannel };

type Screen =
  | { kind: 'ASK'; token: string; channel: ReferralChannel }
  | { kind: 'ASK_RESOLVING'; code: string; channel: ReferralChannel }
  | { kind: 'ASK_BY_CODE' }
  | { kind: 'INBOX'; ref?: RefContext }
  | {
      kind: 'SHARE';
      answerId: string;
      cardImageUrl: string | null;
      storyShareUrl: string | null;
    }
  | { kind: 'LEADERBOARD' }
  | { kind: 'REWARDS' }
  | { kind: 'SETTINGS' }
  | { kind: 'ANALYTICS' }
  | { kind: 'MY_QUESTIONS' }
  | { kind: 'MESSAGES' };

/** Экраны-вкладки, на которых видна нижняя навигация. */
const TAB_SCREENS: ReadonlyArray<Screen['kind']> = [
  'INBOX',
  'LEADERBOARD',
  'REWARDS',
  'SETTINGS',
];

/** Перевод выбранной вкладки Tabbar в состояние экрана. */
function tabToScreen(tab: TabKey): Screen {
  switch (tab) {
    case 'INBOX':
      return { kind: 'INBOX' };
    case 'LEADERBOARD':
      return { kind: 'LEADERBOARD' };
    case 'REWARDS':
      return { kind: 'REWARDS' };
    case 'SETTINGS':
      return { kind: 'SETTINGS' };
  }
}

function initialScreen(): Screen {
  // work-038: `resolveStartParam()`, а НЕ живое значение из SDK — после нашего
  // собственного перезапуска (лечение протухшего пропуска) живого значения нет,
  // и без восстановления человек провалился бы на главный экран вместо того,
  // ради которого пришёл.
  const parsed = parseStartParam(resolveStartParam());
  if (parsed.mode === 'ASK')
    return { kind: 'ASK', token: parsed.token, channel: parsed.channel };
  // work-035: короткий код ссылки — initialScreen СИНХРОННЫЙ, код нельзя
  // резолвить здесь. AskResolvingScreen делает GET /boxes/link/:code на mount
  // и переключает экран на ASK (success) либо INBOX (error, CANON G3).
  if (parsed.mode === 'ASK_RESOLVING')
    return { kind: 'ASK_RESOLVING', code: parsed.code, channel: parsed.channel };
  // AD-режим (реклама привела прямо в мини-апп) — НЕ ask-контекст, обычный
  // INBOX/онбординг; атрибуция отправляется отдельным эффектом (см. ниже).
  return { kind: 'INBOX' };
}

export function App() {
  /**
   * work-038: сервер перестал принимать пропуск Telegram (401 на любом запросе).
   *
   * Подписка — САМЫМ ПЕРВЫМ эффектом, до сетевых: `InboxScreen` шлёт `POST /boxes`
   * раньше, чем смонтируются остальные эффекты, и без ранней подписки первый
   * (единственный) отказ был бы потерян. `subscribeAuthFailure` дополнительно
   * отдаёт уже случившийся отказ сразу при подписке.
   *
   * Хуков контекста здесь быть НЕ МОЖЕТ: дерево провайдеров рендерит сам `App`
   * (см. `return` ниже), то есть `App` не потомок `ToastProvider`/`LocaleProvider`.
   */
  const [authFailure, setAuthFailure] = useState<SessionFailure | null>(null);
  useEffect(() => subscribeAuthFailure(setAuthFailure), []);

  /**
   * work-038: запомнить контекст запуска и разобраться с меткой перезапуска.
   *
   * ПОРЯДОК ВАЖЕН. `resolveStartParam()` мемоизируется при первом вызове — он уже
   * произошёл выше, в инициализаторах `useState` (`initialScreen`,
   * `openedWithoutAskToken`), которые выполняются до эффектов. Поэтому снять метку
   * здесь безопасно: восстановление контекста уже случилось.
   *
   * `clearRestartMark(getAuthDateSec())` снимает метку, только если перезапуск
   * УДАЛСЯ (пропуск обновился). Если Telegram отдал тот же самый `auth_date`
   * (баг Telegram Desktop #28303) — метка остаётся, и экран истёкшей сессии
   * покажет честное «Telegram выдал тот же старый пропуск» вместо предложения
   * жать ту же кнопку по кругу.
   */
  useEffect(() => {
    captureLiveStartParam();
    clearRestartMark(getAuthDateSec());
  }, []);

  // Язык: сначала из localStorage, потом из detect. На mount синхронизируем с сервером.
  const [locale, setLocale] = useState<Locale>(() => readStoredLocale() ?? detectLocale());

  // Направление письма документа (RTL для ar/fa/he) следует за выбранным языком.
  useEffect(() => {
    applyDocumentLocale(locale);
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { locale: serverLocale } = await getLocalePref();
        if (!cancelled && serverLocale !== null) {
          setLocale((cur) => {
            if (serverLocale !== cur) {
              try { localStorage.setItem('veylme.locale', serverLocale); } catch { /* ignore */ }
            }
            return serverLocale;
          });
        }
      } catch {
        // Нет связи или auth fail — оставляем detected/local значение.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /**
   * Смена языка: оптимистичное обновление + сохранение на сервере.
   * При ошибке откатывает state и localStorage обратно, затем бросает —
   * SettingsScreen поймает и покажет t.settings_language_error.
   */
  async function changeLocale(next: Locale): Promise<void> {
    const prev = locale;
    // Оптимистичное обновление
    setLocale(next);
    try { localStorage.setItem('veylme.locale', next); } catch { /* ignore */ }
    try {
      await updateLocalePref(next);
    } catch (err) {
      // Откат
      setLocale(prev);
      try { localStorage.setItem('veylme.locale', prev); } catch { /* ignore */ }
      throw err;
    }
  }

  const [screen, setScreen] = useState<Screen>(initialScreen);
  const [isAdmin, setIsAdmin] = useState(false);
  const [lastStoryShareUrl, setLastStoryShareUrl] = useState<string | null>(null);
  // work-020: открылись БЕЗ ask-токена (parseStartParam → INBOX/AD) → показываем
  // онбординг код-входа на Inbox. Считаем один раз на mount (start_param не меняется).
  // work-035: AD-режим (реклама привела прямо в мини-апп) — тоже «без ask-токена».
  const [openedWithoutAskToken] = useState(() => {
    const mode = parseStartParam(resolveStartParam()).mode;
    return mode === 'INBOX' || mode === 'AD';
  });

  // work-035 (§6 путь 2): AD-режим — отправляем разобранный код на сервер для
  // first-touch атрибуции. Fail-soft (postAdSource сам не бросает наружу
  // ничего критичного, но глушим на всякий случай — ошибка НЕ должна мешать
  // онбордингу). useRef-guard — ровно один вызов за сессию (StrictMode-safe).
  const adSourceSent = useRef(false);
  useEffect(() => {
    if (adSourceSent.current) return;
    // work-038: тоже через `resolveStartParam()` — иначе перезапуск терял бы
    // рекламную метку и мы недосчитывались конверсии по платному трафику.
    // Сервер к поздней атрибуции готов: first-touch `updateMany WHERE
    // adSourceCode IS NULL` (`apps/api/src/services/adSource.ts:57`).
    const parsed = parseStartParam(resolveStartParam());
    if (parsed.mode !== 'AD') return;
    adSourceSent.current = true;
    void postAdSource(parsed.code).catch(() => {
      // best-effort — органика не должна ломаться из-за сбоя атрибуции.
    });
  }, []);

  // Эмит app_opened — ТОЛЬКО в авторском контексте (не ASK / ASK_BY_CODE /
  // ASK_RESOLVING — резолв ещё не завершён, это не автор открыл своё приложение).
  // useRef-guard гарантирует ровно один эмит за сессию (StrictMode-safe).
  const appOpenedEmitted = useRef(false);
  useEffect(() => {
    if (appOpenedEmitted.current) return;
    const kind = screen.kind;
    if (kind === 'ASK' || kind === 'ASK_BY_CODE' || kind === 'ASK_RESOLVING') return;
    appOpenedEmitted.current = true;
    void emitEvent({ type: 'app_opened' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Получаем статус админа один раз на монтировании — ошибку глотаем,
  // при отказе isAdmin остаётся false и панель аналитики просто не показывается.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { isAdmin: admin } = await getAdminStatus();
        if (!cancelled) setIsAdmin(admin);
      } catch {
        // не админ или нет сети — игнорируем
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Точка входа в экран ввода кода (онбординг-баннер Inbox + Cell в Настройках).
  const onEnterCode = () => setScreen({ kind: 'ASK_BY_CODE' });

  // Реактивная тема/платформа для <AppRoot> (Веха 13.1).
  // isDark валиден только когда themeParams смонтирован (см. main.tsx
  // bindThemeCssVars); до mount → дефолт 'dark' (палитра приложения тёмная),
  // чтобы не «мигать» в light. platform — pure-маппинг, покрыт unit-тестом.
  const themeMounted = useSignal(isThemeParamsMounted);
  const dark = useSignal(themeParams.isDark);
  const launchParams = useLaunchParams();
  const appearance: 'light' | 'dark' = themeMounted
    ? dark
      ? 'dark'
      : 'light'
    : 'dark';
  const platform = mapPlatform(launchParams.tgWebAppPlatform);

  function renderScreen() {
    if (screen.kind === 'ASK') {
      return (
        <AskScreen
          token={screen.token}
          channel={screen.channel}
          onCreateOwnBox={() =>
            setScreen({
              kind: 'INBOX',
              ref: { sourceBoxToken: screen.token, channel: screen.channel },
            })
          }
        />
      );
    }

    if (screen.kind === 'ASK_RESOLVING') {
      // work-035: код→token обмен один раз, дальше — обычный ASK-поток
      // (границы `/questions`, `/events`, `ref.sourceBoxToken` видят ТОЛЬКО
      // полный token). Ошибка резолва → INBOX (CANON G3, не падаем).
      return (
        <AskResolvingScreen
          code={screen.code}
          onResolved={(token) => setScreen({ kind: 'ASK', token, channel: screen.channel })}
          onError={() => setScreen({ kind: 'INBOX' })}
        />
      );
    }

    if (screen.kind === 'ASK_BY_CODE') {
      return (
        <AskByCodeScreen
          onClose={() => setScreen({ kind: 'INBOX' })}
          onCreateOwnBox={() => setScreen({ kind: 'INBOX' })}
        />
      );
    }

    if (screen.kind === 'INBOX') {
      return (
        <InboxScreen
          refContext={screen.ref}
          showCodeOnboarding={openedWithoutAskToken}
          onEnterCode={onEnterCode}
          onShareAnswer={(answer) =>
            setScreen({
              kind: 'SHARE',
              answerId: answer.id,
              cardImageUrl: answer.cardImageUrl,
              storyShareUrl: lastStoryShareUrl,
            })
          }
          onBoxLoaded={(storyShareUrl) => setLastStoryShareUrl(storyShareUrl)}
        />
      );
    }

    if (screen.kind === 'LEADERBOARD') {
      return <LeaderboardScreen />;
    }

    if (screen.kind === 'REWARDS') {
      return <RewardsScreen />;
    }

    if (screen.kind === 'SETTINGS') {
      return (
        <SettingsScreen
          onEnterCode={onEnterCode}
          isAdmin={isAdmin}
          onOpenAnalytics={() => setScreen({ kind: 'ANALYTICS' })}
          onOpenMyQuestions={() => setScreen({ kind: 'MY_QUESTIONS' })}
          onOpenMessages={() => setScreen({ kind: 'MESSAGES' })}
        />
      );
    }

    if (screen.kind === 'ANALYTICS') {
      return <AnalyticsScreen onBack={() => setScreen({ kind: 'SETTINGS' })} />;
    }

    if (screen.kind === 'MY_QUESTIONS') {
      return <MyQuestionsScreen onBack={() => setScreen({ kind: 'SETTINGS' })} />;
    }

    if (screen.kind === 'MESSAGES') {
      // work-036 (Трек D): под-поток «Полученные сообщения» из Настроек —
      // тот же InboxScreen, но boxFormat='message' (свой заголовок/пустой
      // список, БЕЗ код-онбординга) + BackButton в Настройки. Ответ на
      // сообщение опционален — onShareAnswer оставлен рабочим (петля роста).
      return (
        <InboxScreen
          boxFormat="message"
          onBack={() => setScreen({ kind: 'SETTINGS' })}
          onShareAnswer={(answer) =>
            setScreen({
              kind: 'SHARE',
              answerId: answer.id,
              cardImageUrl: answer.cardImageUrl,
              storyShareUrl: lastStoryShareUrl,
            })
          }
        />
      );
    }

    return (
      <ShareScreen
        cardImageUrl={screen.cardImageUrl}
        answerId={screen.answerId}
        storyShareUrl={screen.storyShareUrl}
        onBackToInbox={() => setScreen({ kind: 'INBOX' })}
      />
    );
  }

  // work-038: при непринятом пропуске таб-бар гасится — переключать вкладки
  // бессмысленно, любой запрос всё равно получит 401.
  const showTabbar = shouldShowTabbar(screen.kind, authFailure !== null, TAB_SCREENS);

  return (
    // data-veyl-platform / data-veyl-appearance пробрасываются китом на корневой
    // <div> (он спредит неизвестные пропы): по platform styles.css выбирает
    // горизонтальный отступ `--veyl-gutter` (base 22px / ios 16px), по appearance —
    // янтарный `--veyl-warning` (его нет в палитре темы Telegram).
    <AppRoot
      appearance={appearance}
      platform={platform}
      data-veyl-platform={platform}
      data-veyl-appearance={appearance}
    >
      <LocaleProvider value={locale}>
        <SetLocaleProvider value={changeLocale}>
          <ToastProvider>
            {/* work-038: предупреждение о скором истечении пропуска. Ничего не
                рисует — только тост; смонтирован здесь, внутри провайдеров,
                потому что ему нужны `useToast()` и `useT()`. */}
            <SessionAgeWarning />
            {/* На вкладках добавляем нижний отступ, чтобы фиксированный Tabbar
                не перекрывал последние строки контента. */}
            <div className={showTabbar ? styles.withTabbar : undefined}>
              {/* work-038: пропуск не приняли — вместо содержимого честный экран.
                  Врезка именно здесь, а не внутри `renderScreen()`: внутри она не
                  погасила бы таб-бар. Ни один из 11 экранов при этом не тронут. */}
              {authFailure ? (
                <SessionExpiredScreen reason={authFailure.reason} />
              ) : (
                renderScreen()
              )}
            </div>
            {showTabbar && (
              <BottomTabbar
                active={screen.kind as TabKey}
                onSelect={(tab) => setScreen(tabToScreen(tab))}
              />
            )}
          </ToastProvider>
        </SetLocaleProvider>
      </LocaleProvider>
    </AppRoot>
  );
}
